<script setup>
// Aba Atendimento do Painel geral. Os numeros de agora e os tempos medios ja
// vem no payload do Inicio; aqui entram o mapa de calor (o mesmo dos
// relatorios), o movimento por caixa e quem esta esperando resposta.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  formatNumber,
  formatDuration,
  computeTrend,
} from 'kanban/helper/reportFormatters';
import DashboardAPI from 'vinctoHome/api/dashboard';
import KpiCard from 'vinctoHome/components/KpiCard.vue';
import ConversationHeatmapContainer from 'dashboard/routes/dashboard/settings/reports/components/heatmaps/ConversationHeatmapContainer.vue';

const props = defineProps({
  range: { type: String, default: '7d' },
  live: { type: Object, default: () => ({}) },
  summary: { type: Object, default: () => ({}) },
});

const { t, locale } = useI18n();

const detail = ref({ by_inbox: [], waiting: [] });
const isLoading = ref(true);
const hasError = ref(false);

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await DashboardAPI.service({ range: props.range });
    detail.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};
watch(() => props.range, load, { immediate: true });

const kpis = computed(() => {
  const current = props.summary || {};
  const previous = current.previous || {};
  return [
    {
      key: 'received',
      icon: 'i-lucide-inbox',
      label: t('VINCTO_HOME.SERVICE.RECEIVED'),
      value: formatNumber(current.conversations_count, locale.value),
      trend: computeTrend(
        current.conversations_count,
        previous.conversations_count
      ),
    },
    {
      key: 'resolved',
      icon: 'i-lucide-check-check',
      label: t('VINCTO_HOME.KPI.RESOLVED'),
      value: formatNumber(current.resolutions_count, locale.value),
      trend: computeTrend(
        current.resolutions_count,
        previous.resolutions_count
      ),
    },
    {
      key: 'open',
      icon: 'i-lucide-message-circle',
      label: t('VINCTO_HOME.KPI.OPEN'),
      value: formatNumber(props.live?.open, locale.value),
      to: { name: 'home' },
    },
    {
      key: 'unattended',
      icon: 'i-lucide-clock-alert',
      label: t('VINCTO_HOME.KPI.UNATTENDED'),
      value: formatNumber(props.live?.unattended, locale.value),
      accent: !!props.live?.unattended,
      to: { name: 'conversation_unattended' },
    },
    {
      key: 'first_response',
      icon: 'i-lucide-timer',
      label: t('VINCTO_HOME.KPI.FIRST_RESPONSE'),
      value: formatDuration(current.avg_first_response_time, t),
      trend: computeTrend(
        current.avg_first_response_time,
        previous.avg_first_response_time
      ),
      invert: true,
    },
    {
      key: 'resolution_time',
      icon: 'i-lucide-hourglass',
      label: t('VINCTO_HOME.SERVICE.RESOLUTION_TIME'),
      value: formatDuration(current.avg_resolution_time, t),
      trend: computeTrend(
        current.avg_resolution_time,
        previous.avg_resolution_time
      ),
      invert: true,
    },
  ];
});

const waitingFor = since =>
  formatDuration(
    Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000)),
    t
  );
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
      <KpiCard
        v-for="card in kpis"
        :key="card.key"
        :label="card.label"
        :value="card.value"
        :icon="card.icon"
        :trend="card.trend"
        :invert="card.invert"
        :accent="card.accent"
        :to="card.to"
      />
    </div>

    <ConversationHeatmapContainer />

    <p v-if="hasError" class="mb-0 text-sm text-n-ruby-11">
      {{ t('VINCTO_HOME.ERROR') }}
    </p>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section
        class="flex flex-col gap-3 p-[18px] border rounded-2xl border-n-weak bg-white dark:bg-n-solid-2"
      >
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('VINCTO_HOME.SERVICE.BY_INBOX') }}
        </h3>
        <p
          v-if="!isLoading && !detail.by_inbox.length"
          class="mb-0 text-sm text-n-slate-10"
        >
          {{ t('VINCTO_HOME.SERVICE.NO_MOVEMENT') }}
        </p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-xs text-left text-n-slate-10">
              <th class="pb-2 font-medium">
                {{ t('VINCTO_HOME.SERVICE.INBOX') }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{ t('VINCTO_HOME.SERVICE.RECEIVED') }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{ t('VINCTO_HOME.KPI.RESOLVED') }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{ t('VINCTO_HOME.KPI.OPEN') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in detail.by_inbox"
              :key="row.id"
              class="border-t border-n-weak"
            >
              <td class="py-2 truncate text-n-slate-12">{{ row.name }}</td>
              <td class="py-2 text-right text-n-slate-12">
                {{ formatNumber(row.received, locale) }}
              </td>
              <td class="py-2 text-right text-n-slate-12">
                {{ formatNumber(row.resolved, locale) }}
              </td>
              <td class="py-2 text-right text-n-slate-12">
                {{ formatNumber(row.open, locale) }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section
        class="flex flex-col gap-3 p-[18px] border rounded-2xl border-n-weak bg-white dark:bg-n-solid-2"
      >
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('VINCTO_HOME.SERVICE.WAITING') }}
        </h3>
        <p
          v-if="!isLoading && !detail.waiting.length"
          class="mb-0 text-sm text-n-slate-10"
        >
          {{ t('VINCTO_HOME.SERVICE.NOBODY_WAITING') }}
        </p>
        <router-link
          v-for="row in detail.waiting"
          :key="row.display_id"
          :to="{
            name: 'inbox_conversation',
            params: { conversation_id: row.display_id },
          }"
          class="flex items-center justify-between gap-3 px-2 py-2 -mx-2 rounded-lg hover:bg-n-alpha-2"
        >
          <span class="flex flex-col min-w-0">
            <span class="text-sm truncate text-n-slate-12">
              {{ row.contact_name || `#${row.display_id}` }}
            </span>
            <span class="text-xs truncate text-n-slate-10">
              {{ row.inbox_name }}
              <template v-if="row.assignee_name">
                · {{ row.assignee_name }}
              </template>
            </span>
          </span>
          <span class="text-xs font-medium shrink-0 text-n-amber-11">
            {{ waitingFor(row.waiting_since) }}
          </span>
        </router-link>
      </section>
    </div>
  </div>
</template>
